import _ from 'lodash';
import { Reducer } from 'react';
import {
  AnalyticsReport,
  AnalyticsTabItem,
  AnalyticsUiState,
  MockServiceAnalyticsState,
  ReportingViewState,
} from 'store/analytics/ui/analyticsUiTypes';
import { AnalyticsUiActionTypes } from 'store/analytics/ui/analyticsUiActionTypes';
import {
  inspectionViewInitialState,
  inspectionViewReducer,
} from 'store/analytics/ui/subViews/inspectionView/inspectionViewReducer';
import { InspectionViewActionTypes } from 'store/analytics/ui/subViews/inspectionView/inspectionViewActionTypes';
import { ReportingViewActionTypes } from 'store/analytics/ui/subViews/reportingView/reportingViewActionTypes';
import {
  reportConfigInitialState,
  reportConfigReducer,
  reportingViewInitialState,
  reportingViewReducer,
} from 'store/analytics/ui/subViews/reportingView/reportingViewReducer';
import { TRANSACTION_HITS_AND_MISSES } from 'store/analytics/analyticsReportTypes';
import { webhookViewReducer } from 'store/analytics/ui/subViews/webhookView/webhookViewReducer';
import { WebhookViewActionTypes } from './subViews/webhookView/webhookActionTypes';
import { initialHttpViewState } from './subViews/common/initialValues';
import { HttpCallsActionTypes } from './subViews/httpCallsView/httpCallsActionTypes';
import { httpCallsViewReducer } from './subViews/httpCallsView/httpCallsViewReducer';
import { StateChangesActionTypes } from 'store/analytics/ui/subViews/stateChangesView/stateChangesActionTypes';
import {
  initialStateChangesViewState,
  stateChangesViewReducer,
} from 'store/analytics/ui/subViews/stateChangesView/stateChangesViewReducer';

const initialState: AnalyticsUiState = {
  mockServices: [],
  isLoading: false,
  isMockServiceMetadataInitialised: false,
  tabs: [],
  activeTab: null,
  activeUiContext: null,
};

export const initialMockServiceAnalyticsState: MockServiceAnalyticsState = {
  activeTab: 'inspection',
  activeRunnerTab: 'httpRunner',
  inspectionView: inspectionViewInitialState,
  webhookView: { ...initialHttpViewState },
  httpCallView: { ...initialHttpViewState },
  stateChangesView: { ...initialStateChangesViewState },
  reportingView: { ...reportingViewInitialState },
  reportsConfig: { ...reportConfigInitialState },
  sessionTracking: {}, // TODO - add init state for session tracking
};

type AllAnalyticsTypes =
  | AnalyticsUiActionTypes
  | InspectionViewActionTypes
  | ReportingViewActionTypes
  | HttpCallsActionTypes
  | WebhookViewActionTypes
  | StateChangesActionTypes;

/**
 * Analytics UI reducer contains other nested reducers for storing tabs state
 * Nested Reducers Pattern taken from: https://stackoverflow.com/questions/36786244/nested-redux-reducers
 * We just do not change the whole state of the subviews at once but we split it into smaller actions which are handled
 * separately in child reducers.
 *
 * This state is synced with browser local storage to keep the state of all tabs when user leaves virtual services area.
 *
 * @param state
 * @param action
 */
export const analyticsUiReducer: Reducer<AnalyticsUiState, AllAnalyticsTypes> = (
  state = initialState,
  action,
): AnalyticsUiState => {
  switch (action.type) {
    // All subview actions are forwarded to child reducers, which handle them:

    // Webhook tab sub reducer
    case 'SET_WEBHOOK_VIEW_ACTIVE_PAGE':
    case 'SET_WEBHOOK_VIEW_PAGE_SIZE':
    case 'SELECT_WEBHOOK_VIEW_TRIGGER':
    case 'SELECT_WEBHOOK_DETAIL_TAB':
    case 'SET_WEBHOOK_SEARCH_KEYWORD':
      const webhookViewRootState: any = _.cloneDeep(state);
      const webhookViewState = webhookViewRootState.activeUiContext.webhookView;
      webhookViewRootState.activeUiContext.webhookView = webhookViewReducer(webhookViewState, action);
      return webhookViewRootState;

    // Http Calls tab sub reducer
    case 'SELECT_HTTP_VIEW_TRIGGER':
    case 'SET_HTTP_CALLS_ACTIVE_PAGE':
    case 'SELECT_HTTP_CALLS_DETAIL_TAB':
    case 'SET_HTTP_CALLS_PAGE_SIZE':
    case 'SET_HTTP_CALLS_SEARCH_KEYWORD':
      const httpCallViewRootState: any = _.cloneDeep(state);
      const httpCallViewState = httpCallViewRootState.activeUiContext.httpCallView;
      httpCallViewRootState.activeUiContext.httpCallView = httpCallsViewReducer(httpCallViewState, action);
      return httpCallViewRootState;

    // State Changes tab sub reducer
    case 'SELECT_STATE_CHANGES_VIEW_TRIGGER':
    case 'SET_STATE_CHANGES_ACTIVE_PAGE':
    case 'SET_STATE_CHANGES_PAGE_SIZE':
    case 'SET_STATE_CHANGES_SEARCH_KEYWORD':
      const stateChangesViewRootState: any = _.cloneDeep(state);
      const stateChangesViewState = stateChangesViewRootState.activeUiContext.stateChangesView;
      stateChangesViewRootState.activeUiContext.stateChangesView = stateChangesViewReducer(
        stateChangesViewState,
        action,
      );
      return stateChangesViewRootState;

    // Inspection subView actions - for currently active ui context
    case 'SET_INSPECTION_VIEW_ACTIVE_PAGE':
    case 'SET_INSPECTION_VIEW_PAGE_SIZE':
    case 'SELECT_INSPECTION_VIEW_REQUEST':
    case 'SELECT_REQUEST_DETAIL_TAB':
    case 'SET_SEARCH_KEYWORD':
      const inspectionViewRootState: any = _.cloneDeep(state);
      const inspectionViewState = inspectionViewRootState.activeUiContext.inspectionView;
      inspectionViewRootState.activeUiContext.inspectionView = inspectionViewReducer(inspectionViewState, action);
      return inspectionViewRootState;

    // Reporting subView actions - for currently active ui context
    case 'SELECT_REPORT':
    case 'SET_DATE_RANGE':
    case 'SET_FIXED_TIME_RANGE':
    case 'SET_MS_SEARCH_KEYWORD':
    case 'SELECT_MOCK_SERVICES':
      const reportingViewRootState: any = _.cloneDeep(state);

      // Assign reporting view reducer
      const reportingViewState = reportingViewRootState.activeUiContext.reportingView;
      reportingViewRootState.activeUiContext.reportingView = reportingViewReducer(reportingViewState, action);
      return reportingViewRootState;

    case 'SET_RESOLUTION':
      const reportViewRootState: any = _.cloneDeep(state);
      if (reportViewRootState.activeUiContext.reportingView.selectedReport.id === TRANSACTION_HITS_AND_MISSES) {
        const prevReportConfig: ReportingViewState | undefined = _.find(
          reportViewRootState.activeUiContext.reportsConfig.configList,
          (data: { selectedReport: AnalyticsReport }) =>
            data.selectedReport.id === reportViewRootState.activeUiContext.reportingView.selectedReport.id,
        );
        if (
          !_.isNil(prevReportConfig) &&
          prevReportConfig.resolution !== null &&
          (action.payload === '1m' || action.payload === '1H')
        ) {
          action.payload = prevReportConfig.resolution;
        }
      }

      // Assign reporting view reducer
      const reportViewState = reportViewRootState.activeUiContext.reportingView;
      reportViewRootState.activeUiContext.reportingView = reportingViewReducer(reportViewState, action);
      return reportViewRootState;

    case 'SET_REPORT_CONFIG':
      const reportingViewRootConfig: any = _.cloneDeep(state);
      // set its own configuration in each report type
      const reportsConfig = reportingViewRootConfig.activeUiContext.reportsConfig;
      reportingViewRootConfig.activeUiContext.reportsConfig = reportConfigReducer(reportsConfig, action);
      return reportingViewRootConfig;

    case 'ADD_ANALYTICS_TAB':
      /* Add a tab to the array and select it */
      const tabs = _.cloneDeep(state.tabs);
      tabs.push(action.payload);

      return {
        ...state,
        tabs: tabs,
        activeTab: action.payload.id,
        activeUiContext: action.payload.uiState,
      };

    case 'CLEAR_ANALYTICS_TABS':
      return { ...state, tabs: [], activeTab: null };

    case 'SELECT_INSPECTION_VIEW_REQUEST_FOR_TAB': {
      const updatedState = _.cloneDeep(state);
      const updatedTabIndex = state.tabs.findIndex(item => String(item.id) === String(action.payload.tabId));
      if (updatedTabIndex !== -1) {
        updatedState.tabs[updatedTabIndex].uiState.inspectionView.selectedRequest = action.payload.request;
      }
      return updatedState;
    }

    case 'CLOSE_ANALYTICS_TAB':
      // Select tab which is near this one
      const closedTabIndex = state.tabs.findIndex(item => item.id === action.payload);
      let selected = state.activeTab;
      let activeUiContext = _.cloneDeep(state.activeUiContext);

      // Is user closing currently selected tab? We need to add focus to first available tab (left / right)
      if (state.tabs[closedTabIndex].id === selected) {
        if (closedTabIndex !== 0) {
          // Create focus on previous tab
          const prevIndex = closedTabIndex - 1;
          selected = state.tabs[prevIndex].id;
          activeUiContext = state.tabs[prevIndex].uiState;
        } else {
          // Create focus on next tab
          const nextIndex = closedTabIndex + 1;
          const nextElement = state.tabs[nextIndex];
          if (nextElement !== undefined) {
            selected = state.tabs[nextIndex].id;
            activeUiContext = state.tabs[nextIndex].uiState;
          } else {
            selected = null;
            activeUiContext = null;
          }
        }
      }

      // Remove tab from the list
      const filteredTabs = _.cloneDeep(state.tabs).filter(item => item.id !== action.payload);
      return { ...state, tabs: filteredTabs, activeTab: selected, activeUiContext: activeUiContext };

    case 'SELECT_ANALYTICS_TAB': {
      const updatedState = _.cloneDeep(state);

      //Store previous active tab and state before we override it
      const prevSelectedTabId = state.activeTab;
      const prevContentState = _.cloneDeep(state.activeUiContext);

      //Find index of currently selected tab and previously selected tab
      const selectedTabIndex = state.tabs.findIndex(item => item.id === action.payload);
      const previousTabIndex = state.tabs.findIndex(item => item.id === prevSelectedTabId);

      if (selectedTabIndex !== -1 && prevContentState !== null) {
        // Swap content states ( prev <=> current )
        updatedState.activeUiContext = updatedState.tabs[selectedTabIndex].uiState;
        updatedState.tabs[previousTabIndex].uiState = prevContentState;

        // Assign active tab id
        updatedState.activeTab = action.payload;
      } else {
        console.log('Clearing states');
        // Tab is not available, clear selected states
        updatedState.activeTab = null;
        updatedState.activeUiContext = null;
      }

      return updatedState;
    }
    case 'CHANGE_MS_ANALYTICS_CONTENT_TAB': {
      const updatedState = _.cloneDeep(state);
      if (updatedState.activeUiContext !== null) {
        updatedState.activeUiContext.activeTab = action.payload;
      }
      return updatedState;
    }

    case 'SET_ACTIVE_RUNNER_TAB': {
      const updatedState = _.cloneDeep(state);
      if (updatedState.activeUiContext !== null) {
        updatedState.activeUiContext.activeRunnerTab = action.payload;
      }
      return updatedState;
    }

    case 'SET_IS_MOCK_SERVICES_LOADING': {
      return { ...state, isLoading: action.payload };
    }

    case 'SET_IS_MOCK_SERVICE_DATA_INITIALISED': {
      return { ...state, isMockServiceMetadataInitialised: action.payload };
    }

    case 'UPDATE_ANALYTICS_TAB_META': {
      const stateUpdate = _.cloneDeep(state);
      const tabIndex = _.findIndex(
        stateUpdate.tabs,
        (tabItem: AnalyticsTabItem) => String(tabItem.id) === String(action.payload.id),
      );

      if (tabIndex !== -1) {
        stateUpdate.tabs[tabIndex].meta.meta = action.payload.meta;
      }

      return stateUpdate;
    }

    default:
      return state;
  }
};
